import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { MesfModal } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid2,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { Rollset } from "../../models/CampaignData";
import type { StandData } from "../../models/StandData";
import { useChangeRollset } from "../../hooks/useChangeRollset";
import {
	getCompatibleRollsetIds,
	getRollsProduction,
	getRollsets,
} from "../../repositories/MillSetupRepository";
import { ConfirmationDialog } from "./ConfirmationDialog";

interface ChangeRollsetModalProps {
	stand: StandData;
	productSizeId?: number;
	onClose: () => void;
	onSuccess: () => void;
	onError: (message: string) => void;
}

interface FormValues {
	rollsetId: number | null;
	passPosition: number | null;
}

/** Two-column info row matching legacy layout */
const InfoRow2Col = ({
	left,
	leftValue,
	right,
	rightValue,
}: {
	left: string;
	leftValue: string | null | undefined;
	right?: string;
	rightValue?: string | null | undefined;
}) => (
	<Box sx={{ display: "flex", py: 0.25, borderBottom: "1px solid #e5e5e5" }}>
		<Box sx={{ display: "flex", width: right ? "50%" : "100%" }}>
			<Typography sx={{ fontSize: 12, color: "#888", minWidth: 110 }}>
				{left}
			</Typography>
			<Typography sx={{ fontSize: 12, fontWeight: 600 }}>
				{leftValue || "-"}
			</Typography>
		</Box>
		{right && (
			<Box sx={{ display: "flex", width: "50%" }}>
				<Typography sx={{ fontSize: 12, color: "#888", minWidth: 110 }}>
					{right}
				</Typography>
				<Typography sx={{ fontSize: 12, fontWeight: 600 }}>
					{rightValue || "-"}
				</Typography>
			</Box>
		)}
	</Box>
);

const formatTonnage = (val: number | null) =>
	val != null ? val.toFixed(2) : "0.00";

const formatDiam = (val: number | null) => (val != null ? val.toFixed(3) : "-");

/** Legacy sort: cartridges for this stand first, then type priority, then name */
const sortRollsetsLegacy = (
	rollsets: Rollset[],
	standId: number,
): Rollset[] => {
	const typePriority: Record<string, number> = {
		CARTRIDGE: 0,
		ROLLSET: 1,
		FOUNDRY: 2,
	};

	return [...rollsets].sort((a, b) => {
		// 1. Cartridges assigned to THIS stand first
		const aIsStand = a.StandId === standId ? 0 : 1;
		const bIsStand = b.StandId === standId ? 0 : 1;
		if (aIsStand !== bIsStand) return aIsStand - bIsStand;

		// 2. By type priority (cartridge > rollset > foundry)
		const aPrio = typePriority[a.StatusCode?.toUpperCase()] ?? 99;
		const bPrio = typePriority[b.StatusCode?.toUpperCase()] ?? 99;
		if (aPrio !== bPrio) return aPrio - bPrio;

		// 3. By name (numeric-aware sort)
		return (a.Name || "").localeCompare(b.Name || "", undefined, {
			numeric: true,
		});
	});
};

export const ChangeRollsetModal = ({
	stand,
	productSizeId,
	onClose,
	onSuccess,
	onError,
}: ChangeRollsetModalProps) => {
	const [searchInput, setSearchInput] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [selectedRollset, setSelectedRollset] = useState<Rollset | null>(null);
	const [warningMsg, setWarningMsg] = useState<string | null>(null);
	const warningResolveRef = useRef<((value: boolean) => void) | null>(null);

	const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
		defaultValues: {
			rollsetId: null,
			passPosition: null,
		},
	});

	const selectedRollsetId = watch("rollsetId");

	// Every prepared rollset: this stand's ones plus the compatible ones of other stands
	const { data: allPrepared = [], isLoading: isLoadingPrepared } = useQuery({
		queryKey: ["rollsets", "prepared"],
		queryFn: () => getRollsets({ statusCode: "PreparedForLine" }),
	});

	const { data: compatibleIds = [], isLoading: isLoadingCompatible } = useQuery(
		{
			queryKey: ["rollsets", "compatible", stand.Id, productSizeId],
			queryFn: () =>
				getCompatibleRollsetIds({ standId: stand.Id, productSizeId }),
		},
	);
	const compatibleSet = useMemo(() => new Set(compatibleIds), [compatibleIds]);

	const preparedRollsets = useMemo(() => {
		const byName = (a: Rollset, b: Rollset) =>
			(a.Name || "").localeCompare(b.Name || "", undefined, { numeric: true });
		const own = allPrepared
			.filter((rs) => rs.StandId === stand.Id)
			.sort(byName);
		const compatible = allPrepared
			.filter((rs) => rs.StandId !== stand.Id && compatibleSet.has(rs.Id))
			.sort(byName);
		return [...own, ...compatible];
	}, [allPrepared, compatibleSet, stand.Id]);

	const isCompatible = (rs: Rollset) =>
		rs.StandId !== stand.Id && compatibleSet.has(rs.Id);

	// Search rollsets (server-side, when typing ≥2 chars)
	const { data: searchResults = [], isFetching: isSearching } = useQuery({
		queryKey: ["rollsets", "search", searchInput],
		queryFn: () => getRollsets({ search: searchInput }),
		enabled: searchInput.length >= 2,
	});

	// Merge & sort: show search results when searching, otherwise prepared
	const displayRollsets = useMemo(() => {
		if (searchInput.length < 2) return preparedRollsets;
		return sortRollsetsLegacy(searchResults, stand.Id);
	}, [searchInput, searchResults, preparedRollsets, stand.Id]);

	// Fetch barrel layout for selected rollset
	const { data: barrelPasses = [] } = useQuery({
		queryKey: ["rollsProduction", selectedRollsetId],
		queryFn: () => getRollsProduction(selectedRollsetId!),
		enabled: selectedRollsetId != null,
	});

	const availablePasses = useMemo(() => {
		const seen = new Map<number, (typeof barrelPasses)[0]>();
		for (const p of barrelPasses) {
			if (
				(p.PassStatusCode === "G" ||
					p.PassStatusCode === "GOOD" ||
					p.PassStatusCode === "A") &&
				!seen.has(p.PassPosition)
			) {
				seen.set(p.PassPosition, p);
			}
		}
		return [...seen.values()].sort((a, b) => a.PassPosition - b.PassPosition);
	}, [barrelPasses]);

	// Auto-select first pass when rollset changes
	useEffect(() => {
		if (availablePasses.length > 0 && selectedRollsetId) {
			setValue("passPosition", availablePasses[0].PassPosition);
		}
	}, [availablePasses, selectedRollsetId, setValue]);

	// Warning confirmation handlers
	const handleWarning = useCallback((msg: string): Promise<boolean> => {
		return new Promise((resolve) => {
			warningResolveRef.current = resolve;
			setWarningMsg(msg);
		});
	}, []);

	const handleWarningConfirm = useCallback(() => {
		warningResolveRef.current?.(true);
		setWarningMsg(null);
	}, []);

	const handleWarningCancel = useCallback(() => {
		warningResolveRef.current?.(false);
		setWarningMsg(null);
	}, []);

	const { changeRollset } = useChangeRollset({
		onSuccess: () => {
			setIsSubmitting(false);
			onSuccess();
		},
		onError: (err) => {
			setIsSubmitting(false);
			onError(err.message);
		},
		onWarning: handleWarning,
	});

	const onSubmit = (data: FormValues) => {
		if (!data.rollsetId || !data.passPosition) return;

		setIsSubmitting(true);
		changeRollset({
			standId: stand.Id,
			newRollsetId: data.rollsetId,
			newPassPosition: data.passPosition,
			productSizeId,
		});
	};

	const handleRemoveRollset = () => {
		setIsSubmitting(true);
		changeRollset({
			standId: stand.Id,
			newRollsetId: null,
			newPassPosition: null,
		});
	};

	const hasCurrent = stand.RollsetId != null;

	return (
		<>
			<MesfModal
				id="modal-change-rollset"
				title={`Change rolls on stand ${stand.Name}`}
				open={true}
				handleClose={onClose}
				maxWidth="sm"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						{/* ── Current Rollset Section ── */}
						{hasCurrent ? (
							<>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										mb: 1,
									}}
								>
									<Typography variant="body2" sx={{ fontWeight: 700 }}>
										Current cartridge: {stand.CartridgeName || "-"}
									</Typography>
									<Button
										variant="contained"
										color="error"
										size="small"
										onClick={handleRemoveRollset}
										disabled={isSubmitting}
										endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
										sx={{ fontSize: 11, textTransform: "none" }}
									>
										Remove from line
									</Button>
								</Box>
								<Box sx={{ pl: 1, mb: 1 }}>
									<InfoRow2Col
										left="Rollset:"
										leftValue={stand.RollsetName}
										right="Barrel:"
										rightValue={stand.BarrelName}
									/>
									<InfoRow2Col
										left="Top Diameter:"
										leftValue={formatDiam(stand.RollDiameterTop)}
										right="Bottom Diameter:"
										rightValue={formatDiam(stand.RollDiameterBottom)}
									/>
								</Box>
							</>
						) : (
							<Typography
								variant="body2"
								sx={{ textAlign: "center", color: "#888", py: 1 }}
							>
								No current rollset
							</Typography>
						)}

						{/* ── Search Next ── */}
						<Box sx={{ my: 1 }}>
							<Autocomplete
								options={displayRollsets}
								getOptionLabel={(rs) =>
									`${rs.Name}${rs.CartridgeName ? `, Cartridge: ${rs.CartridgeName}` : ""}`
								}
								value={selectedRollset}
								onChange={(_, rs) => {
									setSelectedRollset(rs);
									setValue("rollsetId", rs?.Id ?? null);
									setValue("passPosition", null);
								}}
								inputValue={searchInput}
								onInputChange={(_, val) => setSearchInput(val)}
								loading={
									isLoadingPrepared || isLoadingCompatible || isSearching
								}
								groupBy={
									searchInput.length < 2
										? (rs) =>
												isCompatible(rs)
													? "Compatible, from other stands"
													: `Stand ${stand.Name}`
										: undefined
								}
								isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
								renderOption={(props, rs) => (
									<li {...props} key={rs.Id}>
										<Box
											sx={{ color: isCompatible(rs) ? "#94a3b8" : undefined }}
										>
											<Typography
												sx={{ fontSize: 13, fontWeight: 600, color: "inherit" }}
											>
												{rs.Name}
												{rs.CartridgeName
													? `, Cartridge: ${rs.CartridgeName}`
													: ""}
											</Typography>
											<Typography
												sx={{
													fontSize: 11,
													color: isCompatible(rs) ? "inherit" : "#888",
												}}
											>
												{rs.StandId !== stand.Id && rs.StandName
													? `${rs.StandName} · `
													: ""}
												{rs.BarrelName || ""} ({rs.StatusName})
											</Typography>
										</Box>
									</li>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Search next"
										placeholder="Type to search or click ▼"
										size="small"
										autoFocus
									/>
								)}
								noOptionsText={
									searchInput.length >= 2
										? "No rollsets found"
										: "No prepared rollsets"
								}
								filterOptions={(x) => x}
								fullWidth
							/>
						</Box>

						{/* ── Next Cartridge Section (after selecting) ── */}
						{selectedRollset && (
							<>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										mb: 1,
										mt: 1,
									}}
								>
									<Typography variant="body2" sx={{ fontWeight: 700 }}>
										Next cartridge: {selectedRollset.CartridgeName || "-"}
									</Typography>
									<Button
										variant="contained"
										color="success"
										size="small"
										type="submit"
										disabled={isSubmitting}
										startIcon={
											isSubmitting ? (
												<CircularProgress size={14} />
											) : (
												<ArrowBackIcon sx={{ fontSize: 14 }} />
											)
										}
										sx={{ fontSize: 11, textTransform: "none" }}
									>
										Place in line
									</Button>
								</Box>
								<Box sx={{ pl: 1 }}>
									<InfoRow2Col
										left="Rollset:"
										leftValue={selectedRollset.Name}
										right="Barrel:"
										rightValue={selectedRollset.BarrelName}
									/>
									<InfoRow2Col
										left="Top Roll:"
										leftValue={selectedRollset.TopRollName}
										right="Bottom Roll:"
										rightValue={selectedRollset.BottomRollName}
									/>
									<InfoRow2Col
										left="Status:"
										leftValue={selectedRollset.StatusName}
									/>
								</Box>

								{/* Starter Pass dropdown */}
								<Box sx={{ mt: 1.5 }}>
									<Controller
										name="passPosition"
										control={control}
										rules={{ required: "Select starter pass" }}
										render={({ field, fieldState: { error } }) => (
											<TextField
												select
												label="Starter Pass"
												value={field.value ?? ""}
												onChange={(e) => field.onChange(Number(e.target.value))}
												fullWidth
												size="small"
												error={!!error}
												helperText={error?.message}
											>
												{availablePasses.map((p) => (
													<MenuItem
														key={p.PassPosition}
														value={p.PassPosition}
														title={`Position: ${p.PassPosition} | Name: ${p.PassName} | Scheduled: ${formatTonnage(p.ScheduledTonnage)} tons | Rolled: ${formatTonnage(p.RolledTonnage)} tons`}
													>
														{p.PassPosition} - {p.PassName} -{" "}
														{formatTonnage(p.ScheduledTonnage)}/
														{formatTonnage(p.RolledTonnage)}
													</MenuItem>
												))}
											</TextField>
										)}
									/>
								</Box>
							</>
						)}
					</MesfModal.Content>

					<MesfModal.Actions style={{ padding: "10px 30px 20px" }}>
						<Button
							fullWidth
							variant="contained"
							color="inherit"
							onClick={onClose}
						>
							Close
						</Button>
					</MesfModal.Actions>
				</form>
			</MesfModal>

			<ConfirmationDialog
				open={warningMsg !== null}
				title="Rollset change confirmation"
				message={warningMsg || ""}
				onConfirm={handleWarningConfirm}
				onCancel={handleWarningCancel}
			/>
		</>
	);
};
